import { packet, isPacket, numberInRange } from './client-embed-protocol';

export function installClientUI(win = window) {
  const doc = win.document;
  const root = doc.getElementById('clientBookingRoot');
  if (!root || win.TeboleClient) return;
  const embedded = win.parent !== win;
  let parentOrigin = null;
  let lastHeight = 0;
  let scheduled = false;
  let modalTrigger = null;
  let invalidScheduled = false;
  let parentViewHeight = 0;
  const $ = win.jQuery;
  const visible = el => !!el && el.isConnected && !!el.getClientRects().length;
  const post = (type, data) => {
    if (embedded) win.parent.postMessage(packet(type, data), parentOrigin || '*');
  };
  const measure = () => {
    const height = Math.ceil(root.getBoundingClientRect().bottom + win.scrollY + 24);
    if (height !== lastHeight) { lastHeight = height; post('height', { height }); }
  };
  const resize = () => {
    if (scheduled) return;
    scheduled = true;
    win.requestAnimationFrame(() => { scheduled = false; measure(); });
  };
  const ownViewport = () => {
    if (parentOrigin) return;
    const viewport = win.visualViewport;
    doc.body.style.setProperty('--client-view-top', (viewport ? viewport.offsetTop : 0) + 'px');
    doc.body.style.setProperty('--client-view-height', (viewport ? viewport.height : win.innerHeight) + 'px');
  };
  const announce = doc.createElement('div');
  announce.className = 'sr-only';
  announce.setAttribute('aria-live', 'polite');
  announce.setAttribute('aria-atomic', 'true');
  doc.body.appendChild(announce);

  function reveal(element, options = {}) {
    if (!visible(element)) return;
    if (options.focus) {
      if (!element.matches('input,select,textarea,button,a,[tabindex]')) element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
    }
    // Also lets browsers reveal the iframe in a scrollable embedding page.
    element.scrollIntoView({ block: options.block || 'start', inline: 'nearest', behavior: 'auto' });
    post('reveal', { top: Math.max(0, element.getBoundingClientRect().top + win.scrollY), scrollTop: Math.max(0, win.scrollY) });
    if (embedded && options.navigation && !parentOrigin) {
      // Retain compatibility with existing restaurant embed listeners.
      win.parent.postMessage('scrollToTop', '*');
      win.parent.postMessage(JSON.stringify({ event: 'scrollToTop' }), '*');
    }
    resize();
  }

  function invalid(element) {
    if (!element) return;
    const step = element.closest('.wizard-step');
    if (step && !step.classList.contains('active') && typeof win.goToStep === 'function') {
      win.goToStep(1, element.closest('form').id === 'clientFormJa' ? 'Ja' : '');
    }
    const picker = element.closest('.bootstrap-select');
    const field = picker ? picker.querySelector('button') : element;
    if (!visible(field)) return;
    field.focus({ preventScroll: true });
    reveal(field.closest('.validation-group,.form-group') || field);
  }

  win.TeboleClient = {
    reveal, resize,
    step(form, heading, message) {
      const pane = form.closest('.tab-pane') || doc;
      pane.querySelectorAll('.step-dot').forEach(dot => {
        if (dot.classList.contains('active')) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
      });
      announce.textContent = message;
      reveal(heading, { focus: true, navigation: true });
    }
  };
  if (embedded) {
    doc.body.classList.add('client-embedded');
    win.addEventListener('message', event => {
      if (event.source !== win.parent || event.origin === 'null' || !isPacket(event.data)) return;
      if (parentOrigin && event.origin !== parentOrigin) return;
      const data = event.data;
      if (data.type === 'init') { parentOrigin = event.origin; lastHeight = 0; resize(); }
      if (parentOrigin && data.type === 'viewport' && numberInRange(data.top, 0, 50000) && numberInRange(data.height, 0, 50000)) {
        // A hidden frame must not collapse its dialogs; next visible update corrects it.
        if (data.height > 0) {
          parentViewHeight = data.height;
          doc.body.style.setProperty('--client-view-top', data.top + 'px');
          doc.body.style.setProperty('--client-view-height', Math.max(120, data.height) + 'px');
        }
      }
    });
    post('ready');
  }
  ownViewport();
  win.addEventListener('resize', () => { ownViewport(); resize(); }, { passive: true });
  if (win.visualViewport) win.visualViewport.addEventListener('resize', ownViewport, { passive: true });
  if (win.ResizeObserver) new win.ResizeObserver(resize).observe(root);
  const paymentMessage = doc.getElementById('payment-message');
  if (paymentMessage) {
    paymentMessage.setAttribute('role', 'alert');
    if (win.MutationObserver) new win.MutationObserver(() => {
      if (paymentMessage.textContent.trim()) reveal(paymentMessage);
    }).observe(paymentMessage, { childList: true, characterData: true, subtree: true });
  }
  doc.addEventListener('invalid', event => {
    if (invalidScheduled) return;
    invalidScheduled = true;
    win.requestAnimationFrame(() => { invalidScheduled = false; invalid(event.target); });
  }, true);
  if ($) {
    $('form').on('invalid-form.clientUI', (event, validator) => {
      const first = validator.errorList && validator.errorList[0];
      if (first) win.requestAnimationFrame(() => invalid(first.element));
    });
    $(doc).on('show.bs.modal', '.modal', function() {
      modalTrigger = doc.activeElement;
      doc.body.appendChild(this);
      this.setAttribute('aria-modal', 'true');
      this.querySelectorAll('.close').forEach(button => button.setAttribute('aria-label', doc.documentElement.lang === 'ja' ? '閉じる' : 'Close'));
      if (embedded && (!parentOrigin || parentViewHeight < 280)) reveal(root, { navigation: true });
    });
    $(doc).on('hidden.bs.modal', '.modal', () => {
      if (visible(modalTrigger)) modalTrigger.focus({ preventScroll: true });
      resize();
    });
    $(doc).on('shown.bs.tab', () => { resize(); });
    $(doc).ajaxComplete(resize);
  }
  win.addEventListener('pageshow', event => {
    if (!event.persisted || !$) return;
    $('form').each(function() {
      if (!$(this).data('is-submitting')) return;
      $(this).removeData('is-submitting').find('[type="submit"]').css({ 'pointer-events': '', opacity: '' });
    });
    $('#global-loader').hide();
    resize();
  });
  const page = doc.body.getAttribute('data-client-page') || '';
  const start = () => {
    resize();
    const error = Array.from(root.querySelectorAll('.alert-danger,.deposit-error')).find(visible);
    if (error) reveal(error, { focus: true, navigation: true });
    else if (page && page !== 'booking_form') {
      const complete = root.querySelector('.modern-card-complete,.complete-title');
      reveal(complete || root.querySelector('.shop-title,h1,h2') || root, { focus: true, navigation: true });
    }
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}

if (typeof window !== 'undefined') installClientUI();
